/**
 * Safari with privacy options will have localStorage but won't let us write to it.
 * Android WebView might not have localStorage at all.
 * If app is embedded in an iframe, then in Chrome Incognito accessing localStorage throws.
 */

import * as log from 'src/lib/log';

interface StorageOptions {
  session?: boolean;
}

const memoryStorage: Record<string, string> = {};

export const readItem = (key: string, options?: StorageOptions): string | undefined => {
  let value: string | null;

  try {
    const browserStorage = getBrowserStorage(options);
    if (browserStorage) {
      value = browserStorage.getItem(key);
    } else {
      value = memoryStorage[key] || null;
    }
  } catch {
    log.warning('Browser storage is not available, error reading');
    value = null;
  }

  return value !== null ? value : undefined;
};

export const writeItem = (key: string, value: string, options?: StorageOptions): void => {
  try {
    const browserStorage = getBrowserStorage(options);
    if (browserStorage) {
      browserStorage.setItem(key, value);
    } else {
      memoryStorage[key] = value;
    }
  } catch {
    log.warning('Browser storage is not available, error writing');
    memoryStorage[key] = value;
  }
};

export const removeItem = (key: string, options?: StorageOptions): void => {
  try {
    const browserStorage = getBrowserStorage(options);
    if (browserStorage) {
      browserStorage.removeItem(key);
    } else {
      delete memoryStorage[key];
    }
  } catch {
    log.warning('Browser storage is not available, error removing');
  }
};

const getBrowserStorage = (options?: StorageOptions): Storage | null => {
  const { session } = options || {};
  if (session) {
    return checkStorageAvailable('sessionStorage') ? window.sessionStorage : null;
  }

  return checkStorageAvailable('localStorage') ? window.localStorage : null;
};

// see: https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#testing_for_availability
const checkStorageAvailable = (type: 'localStorage' | 'sessionStorage'): boolean => {
  let storage: Storage | undefined;
  try {
    storage = window[type];
    const x = '__anyword_storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return !!(
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
};
